import { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getModuleIvCurve } from "../services/api";
import { useTranslateText } from "../context/LanguageContext";

const DEFAULT_QUERY = {
  effective_irradiance: 1000,
  ambient_temperature_c: 25,
  wind_speed_mps: 1,
  voltage_points: 120,
};

function toNum(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function formatDisplayNumber(value, digits = 2) {
  const num = toNum(value);
  return num == null
    ? null
    : num
        .toFixed(digits)
        .replace(/\.00$/, "")
        .replace(/(\.\d*[1-9])0+$/, "$1");
}

function formatDisplayValue(value, unit = "", digits = 2) {
  if (value == null || value === "") return "-";
  const formatted =
    typeof value === "number" || !Number.isNaN(Number(value))
      ? formatDisplayNumber(value, digits)
      : String(value);
  return formatted == null ? "-" : `${formatted}${unit ? ` ${unit}` : ""}`;
}

function formatSaturationCurrentValue(value) {
  const num = toNum(value);
  if (num == null) return "-";
  return `${formatDisplayNumber(num * 1e9, 6) ?? "0"} nA`;
}

function getFieldDisplayDigits(field) {
  const label = String(field?.label || field?.key || "").trim().toLowerCase();
  if (label === "saturation current i_o_ref" || label === "saturation current") {
    return 6;
  }
  if (
    label === "temperature coeff. of isc" ||
    label === "temperature coeff. of voc" ||
    label === "power temp. coeff."
  ) {
    return 4;
  }
  return 2;
}

function formatInputLabel(key) {
  const overrides = {
    effective_irradiance: "Effective Irradiance",
    ambient_temperature_c: "Ambient Temperature",
    cell_temperature_c: "Cell Temperature",
    wind_speed_mps: "Wind Speed",
    voltage_points: "Voltage Points",
  };
  if (overrides[key]) return overrides[key];
  return String(key || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function buildInitialDraft() {
  return Object.fromEntries(
    Object.entries(DEFAULT_QUERY).map(([key, value]) => [key, String(value)])
  );
}

function buildDraftFromInputs(inputs) {
  const next = buildInitialDraft();
  Object.entries(inputs || {}).forEach(([key, spec]) => {
    next[key] =
      spec?.value == null
        ? ""
        : formatDisplayNumber(spec.value) ?? String(spec.value);
  });
  return next;
}

function curveToChartData(series, valueKey) {
  return (Array.isArray(series) ? series : []).map((point) => ({
    x: toNum(point?.x) ?? 0,
    [valueKey]: toNum(point?.y) ?? 0,
  }));
}

function findPeakPoint(series) {
  if (!Array.isArray(series) || !series.length) return null;
  return series.reduce((best, point) => {
    if (!best) return point;
    return (toNum(point?.y) ?? 0) > (toNum(best?.y) ?? 0) ? point : best;
  }, null);
}

function findFieldValue(sections, labels) {
  const normalizedLabels = labels.map((label) => String(label).trim().toLowerCase());
  for (const section of Array.isArray(sections) ? sections : []) {
    for (const field of Array.isArray(section?.fields) ? section.fields : []) {
      const label = String(field?.label || field?.key || "").trim().toLowerCase();
      if (normalizedLabels.includes(label)) return field?.value;
    }
  }
  return null;
}

function KpiChip({ label, value, unit, isLast = false }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Box
      sx={{
        px: 1.18,
        py: 0.46,
        minHeight: 42,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        borderRight: {
          xs: "none",
          md: isLast ? "none" : "1px solid rgba(148,163,184,0.18)",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: isDark ? "#94a3b8" : "#6b7280",
          letterSpacing: "0.08em",
          fontSize: "0.64rem",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mt: 0.04,
          fontWeight: 700,
          fontSize: "0.92rem",
          lineHeight: 1.15,
          color: isDark ? "#f1f5f9" : "#111827",
        }}
      >
        {formatDisplayValue(value, unit)}
      </Typography>
    </Box>
  );
}

function ReadonlySection({ section }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.4,
        borderRadius: 2.4,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: "0.88rem" }}>
        {section?.title || "Section"}
      </Typography>
      <Stack spacing={0.65}>
        {(section?.fields || []).map((field) => {
          const label = String(field?.label || field?.key || "").trim().toLowerCase();
          const isSaturationCurrent =
            label === "saturation current i_o_ref" || label === "saturation current";
          return (
            <Box
              key={field?.key || field?.label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                py: 0.5,
                borderBottom: "1px dashed rgba(148,163,184,0.18)",
                "&:last-of-type": {
                  borderBottom: "none",
                  pb: 0,
                },
              }}
            >
              <Typography variant="body2" sx={{ color: isDark ? "#7eb3e8" : "#48678b", fontSize: "0.8rem" }}>
                {field?.label || field?.key || "-"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  textAlign: "right",
                  fontSize: "0.8rem",
                  color: isDark ? "#e2e8f0" : "#163250",
                  wordBreak: "break-word",
                }}
              >
                {isSaturationCurrent
                  ? formatSaturationCurrentValue(field?.value)
                  : formatDisplayValue(field?.value, field?.unit, getFieldDisplayDigits(field))}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

function CurveCard({
  title,
  xLabel,
  yLabel,
  data,
  dataKey,
  lineColor,
  highlight,
  highlightLabel,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const chartAxisFill = isDark ? "#94a3b8" : "#475569";
  const chartLabelFill = isDark ? "#cbd5e1" : "#334155";
  const chartDotLabelFill = isDark ? "#e2e8f0" : "#0f172a";
  const chartGridStroke = isDark ? "rgba(100,116,139,0.25)" : "rgba(148,163,184,0.25)";

  const yValues = data.map((point) => toNum(point?.[dataKey]) ?? 0);
  const maxY = yValues.length ? Math.max(...yValues) : 0;
  const yUpperBound = maxY > 0 ? Number((maxY * 1.1).toFixed(2)) : "auto";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        background: isDark
          ? "linear-gradient(180deg, rgba(15,23,42,0.8) 0%, rgba(30,41,59,0.9) 100%)"
          : "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,1) 100%)",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.1, fontSize: "0.92rem" }}>
        {title}
      </Typography>
      <Box
        sx={{
          height: 320,
          "& .recharts-cartesian-grid-horizontal line, & .recharts-cartesian-grid-vertical line": {
            stroke: chartGridStroke,
          },
          "& .recharts-cartesian-axis-tick-value, & .recharts-text": {
            fill: chartAxisFill,
            fontSize: 13,
          },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 18, left: 36, bottom: 12 }}>
            <CartesianGrid strokeDasharray="4 4" />
            <XAxis
              type="number"
              dataKey="x"
              domain={[0, "dataMax"]}
              tickFormatter={(value) => formatDisplayNumber(value) ?? ""}
              label={{
                value: xLabel,
                position: "insideBottomRight",
                offset: -4,
                fontSize: 14,
                fontWeight: 600,
                fill: chartLabelFill,
              }}
            />
            <YAxis
              width={80}
              domain={[0, yUpperBound]}
              tickFormatter={(value) => formatDisplayNumber(value) ?? ""}
              label={{
                value: yLabel,
                angle: -90,
                position: "insideLeft",
                dx: -26,
                fontSize: 14,
                fontWeight: 700,
                fill: chartLabelFill,
              }}
            />
            <Tooltip
              formatter={(value) => formatDisplayNumber(value) ?? "-"}
              labelFormatter={(value) => `${xLabel}: ${formatDisplayNumber(value) ?? "-"}`}
              contentStyle={{
                backgroundColor: isDark ? "#1e293b" : "#ffffff",
                border: isDark ? "1px solid rgba(100,116,139,0.4)" : "1px solid #e2e8f0",
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
              labelStyle={{ color: isDark ? "#94a3b8" : "#475569", fontSize: 12 }}
              itemStyle={{ color: isDark ? "#e2e8f0" : "#0f172a", fontSize: 13, fontWeight: 600 }}
              cursor={{ stroke: isDark ? "rgba(148,163,184,0.3)" : "rgba(100,116,139,0.2)", strokeWidth: 1 }}
            />
            <Line
              type="monotone"
              dataKey={dataKey}
              name={yLabel}
              stroke={lineColor}
              strokeWidth={2.7}
              dot={false}
              isAnimationActive={false}
            />
            {highlight ? (
              <ReferenceDot
                x={toNum(highlight?.x) ?? 0}
                y={toNum(highlight?.y) ?? 0}
                r={5}
                fill={lineColor}
                stroke={isDark ? "#1e293b" : "#ffffff"}
                strokeWidth={2}
                label={{
                  value: highlightLabel,
                  position: "top",
                  fill: chartDotLabelFill,
                  fontSize: 12,
                }}
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}

export default function ModulePerformanceDialog({ moduleName, accessToken }) {
  const translateText = useTranslateText();
  const [payload, setPayload] = useState(null);
  const [draftValues, setDraftValues] = useState(buildInitialDraft);
  const [requestConfig, setRequestConfig] = useState(DEFAULT_QUERY);
  const [inferCellTemperature, setInferCellTemperature] = useState(true);
  const [loading, setLoading] = useState(true);
  const [requestError, setRequestError] = useState("");
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    setPayload(null);
    setDraftValues(buildInitialDraft());
    setRequestConfig(DEFAULT_QUERY);
    setInferCellTemperature(true);
    setLoading(true);
    setRequestError("");
    setValidationError("");
  }, [moduleName]);

  useEffect(() => {
    if (!moduleName) {
      setLoading(false);
      setRequestError("No module selected.");
      return;
    }

    let active = true;

    async function run() {
      setLoading(true);
      setRequestError("");
      setValidationError("");

      try {
        const data = await getModuleIvCurve(moduleName, requestConfig, accessToken);
        if (!active) return;
        setPayload(data);
        setDraftValues(buildDraftFromInputs(data?.inputs));
        setInferCellTemperature((data?.inputs?.cell_temperature_c?.source || data?.meta?.temperature_source || "") !== "direct");
      } catch (err) {
        if (!active) return;
        if (err?.status === 400) {
          setValidationError(err.message || "Invalid operating conditions.");
        } else if (err?.status === 404) {
          setRequestError("Module not found or not visible for this organization.");
        } else {
          setRequestError(err?.message || "Failed to load module curve.");
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    run();

    return () => {
      active = false;
    };
  }, [accessToken, moduleName, requestConfig]);

  const summary = payload?.summary || {};
  const inputs = payload?.inputs || {};
  const readonlySections = useMemo(
    () => (Array.isArray(payload?.readonly_sections) ? payload.readonly_sections : []),
    [payload?.readonly_sections]
  );
  const calculatedSectionIndex = readonlySections.findIndex(
    (section) => String(section?.title || "").trim().toLowerCase() === "calculated operating point"
  );
  const calculatedSection =
    calculatedSectionIndex >= 0 ? readonlySections[calculatedSectionIndex] : null;
  const mainReadonlySections =
    calculatedSectionIndex >= 0
      ? readonlySections.filter((_, index) => index !== calculatedSectionIndex)
      : readonlySections;
  const ivCurve = payload?.charts?.iv_curve || {};
  const pvCurve = payload?.charts?.pv_curve || {};
  const ivData = useMemo(() => curveToChartData(ivCurve.series, "current"), [ivCurve.series]);
  const pvData = useMemo(() => curveToChartData(pvCurve.series, "power"), [pvCurve.series]);
  const pvPeak = useMemo(() => findPeakPoint(pvCurve.series), [pvCurve.series]);
  const stcSummary = useMemo(
    () => ({
      voc_v:
        findFieldValue(readonlySections, ["Open Circuit Voltage", "Voc", "Voc0"]) ?? summary.voc_v,
      isc_a:
        findFieldValue(readonlySections, ["Short Circuit Current", "Isc", "Isc0"]) ?? summary.isc_a,
      vmp_v:
        findFieldValue(readonlySections, ["Voltage at Pmax", "Vmp", "Vmp0"]) ?? summary.vmp_v,
      imp_a:
        findFieldValue(readonlySections, ["Current at Pmax", "Imp", "Imp0"]) ?? summary.imp_a,
      pmp_w:
        findFieldValue(readonlySections, ["Maximum Power", "Pstc", "STC", "Nominal Power"]) ?? summary.pmp_w,
    }),
    [readonlySections, summary.imp_a, summary.isc_a, summary.pmp_w, summary.vmp_v, summary.voc_v]
  );

  const handleInputChange = (key, value) => {
    setDraftValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleInferModeChange = (event) => {
    const checked = event.target.checked;
    setInferCellTemperature(checked);
  };

  const handleCalculate = () => {
    const nextConfig = {
      effective_irradiance:
        draftValues.effective_irradiance !== "" && draftValues.effective_irradiance != null
          ? draftValues.effective_irradiance
          : DEFAULT_QUERY.effective_irradiance,
      ambient_temperature_c:
        draftValues.ambient_temperature_c !== "" && draftValues.ambient_temperature_c != null
          ? draftValues.ambient_temperature_c
          : DEFAULT_QUERY.ambient_temperature_c,
      wind_speed_mps:
        draftValues.wind_speed_mps !== "" && draftValues.wind_speed_mps != null
          ? draftValues.wind_speed_mps
          : DEFAULT_QUERY.wind_speed_mps,
      voltage_points:
        draftValues.voltage_points !== "" && draftValues.voltage_points != null
          ? draftValues.voltage_points
          : DEFAULT_QUERY.voltage_points,
    };

    if (!inferCellTemperature && draftValues.cell_temperature_c !== "" && draftValues.cell_temperature_c != null) {
      nextConfig.cell_temperature_c = draftValues.cell_temperature_c;
    }

    setRequestConfig(nextConfig);
  };

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Stack spacing={2.05}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1, fontSize: "1.25rem" }}>
          {payload?.name || moduleName || "Selected module"}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.45, fontSize: "0.84rem" }}>
          Default conditions on open are STC. Adjust the operating inputs and click Calculate to
          run the curve model.
        </Typography>
      </Box>

      {requestError ? <Alert severity="error">{requestError}</Alert> : null}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            pt: 0.5,
            minWidth: "fit-content",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 800,
              letterSpacing: "0.08em",
              color: isDark ? "#94a3b8" : "#334155",
              textTransform: "uppercase",
              lineHeight: 1.15,
            }}
          >
            STC
          </Typography>
          <Typography sx={{ fontSize: "0.73rem", color: "text.secondary", mt: 0.15, lineHeight: 1.15 }}>
            1000 W/m2, 25 C
          </Typography>
        </Box>

        <Box
          sx={{
            width: "fit-content",
            maxWidth: "100%",
            p: 0.16,
            borderRadius: 2.15,
            border: isDark ? "1px solid rgba(100,116,139,0.3)" : "1px solid rgba(226,232,240,1)",
            backgroundColor: isDark ? "rgba(15,23,42,0.4)" : "#fbfcfd",
            boxShadow: "0 6px 14px rgba(15,23,42,0.028)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(110px, 1fr))", md: "repeat(5, minmax(132px, 176px))" },
              gap: { xs: 0.25, md: 0 },
            }}
          >
            <KpiChip label="Voc" value={stcSummary.voc_v} unit="V" />
            <KpiChip label="Isc" value={stcSummary.isc_a} unit="A" />
            <KpiChip label="Vmp" value={stcSummary.vmp_v} unit="V" />
            <KpiChip label="Imp" value={stcSummary.imp_a} unit="A" />
            <KpiChip label="Pmp" value={stcSummary.pmp_w} unit="W" isLast />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "360px minmax(0, 1fr)" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Stack spacing={1.25}>
          {mainReadonlySections.map((section, index) => (
            <ReadonlySection key={section?.title || `section-${index}`} section={section} />
          ))}
        </Stack>

        <Stack spacing={2}>
          {loading && !payload ? (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <CircularProgress size={22} />
              <Typography variant="body2">Loading module curve…</Typography>
            </Paper>
          ) : null}

          {payload ? (
            <>
              <CurveCard
                title={translateText(ivCurve.title || "I-V Curve")}
                xLabel={translateText(ivCurve.x_label || "Voltage (V)")}
                yLabel={translateText(ivCurve.y_label || "Current (A)")}
                data={ivData}
                dataKey="current"
                lineColor="#0f766e"
                highlight={
                  summary.vmp_v != null && summary.imp_a != null
                    ? { x: summary.vmp_v, y: summary.imp_a }
                    : null
                }
                highlightLabel="MPP"
              />

              <CurveCard
                title={translateText(pvCurve.title || "P-V Curve")}
                xLabel={translateText(pvCurve.x_label || "Voltage (V)")}
                yLabel={translateText(pvCurve.y_label || "Power (W)")}
                data={pvData}
                dataKey="power"
                lineColor="#ea580c"
                highlight={
                  summary.vmp_v != null && summary.pmp_w != null
                    ? { x: summary.vmp_v, y: summary.pmp_w }
                    : pvPeak
                }
                highlightLabel="Peak power"
              />
            </>
          ) : null}
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "360px minmax(0, 1fr)" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Box>
          {calculatedSection ? <ReadonlySection section={calculatedSection} /> : null}
        </Box>

        <Paper
          elevation={0}
          sx={{
            pt: 1.1,
            px: 1.1,
            pb: 0.45,
            minHeight: 280,
            borderRadius: 2.2,
            border: "1px solid",
            borderColor: "divider",
            background: isDark
              ? "linear-gradient(180deg, rgba(15,23,42,0.8) 0%, rgba(30,41,59,0.9) 100%)"
              : "linear-gradient(180deg, rgba(248,250,252,0.95) 0%, rgba(255,255,255,1) 100%)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              mb: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.88rem" }}>
                Operating inputs
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.74rem" }}>
                STC defaults: 1000 W/m2, 25 C, 1 m/s, 120 voltage points.
              </Typography>
              <Box sx={{ mt: 0.25 }}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={inferCellTemperature}
                      onChange={handleInferModeChange}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.76rem", color: isDark ? "#94a3b8" : "#475569" }}>
                      Infer cell temperature from ambient temperature and wind speed
                    </Typography>
                  }
                />
              </Box>
            </Box>
          </Box>

          {validationError ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {validationError}
            </Alert>
          ) : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: 0.7,
            }}
          >
            {Object.entries(inputs).map(([key, spec]) => {
              const unit = spec?.unit ? ` (${spec.unit})` : "";
              const isCellTemp = key === "cell_temperature_c";
              const isAmbient = key === "ambient_temperature_c";
              const isWind = key === "wind_speed_mps";
              const helperTextByKey = {
                effective_irradiance: "Incident irradiance applied to the module plane.",
                ambient_temperature_c: inferCellTemperature
                  ? "Used with wind speed to infer cell temperature."
                  : "Read-only while direct cell temperature is enabled.",
                cell_temperature_c: inferCellTemperature
                  ? "Read-only inferred value from thermal model."
                  : "Direct cell temperature used for the calculation.",
                wind_speed_mps: inferCellTemperature
                  ? "Used with ambient temperature to infer cell temperature."
                  : "Read-only while direct cell temperature is enabled.",
                voltage_points: "Curve resolution: more points give a smoother trace.",
              };
              const helperText =
                helperTextByKey[key] || "Applied when you run Calculate.";
              const isTemperatureModeField = isCellTemp || isAmbient || isWind;
              const computedDisabled = isTemperatureModeField
                ? inferCellTemperature
                  ? isCellTemp
                  : isAmbient || isWind
                : !spec?.editable;

              return (
                <TextField
                  key={key}
                  fullWidth
                  type="number"
                  size="small"
                  label={`${formatInputLabel(key)}${unit}`}
                  value={draftValues[key] ?? ""}
                  onChange={(event) => handleInputChange(key, event.target.value)}
                  disabled={computedDisabled}
                  helperText={helperText}
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: 1.45,
                      backgroundColor: isDark ? "rgba(30,41,59,0.6)" : "rgba(255,255,255,0.98)",
                    },
                    "& .MuiFormHelperText-root": {
                      marginLeft: 0,
                      marginRight: 0,
                      marginTop: 0.3,
                      fontSize: "0.7rem",
                      lineHeight: 1.2,
                    },
                  }}
                />
              );
            })}
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "stretch", md: "flex-end" },
                alignItems: "flex-end",
              }}
            >
              <Button
                variant="contained"
                onClick={handleCalculate}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 999,
                  px: 2.1,
                  py: 0.6,
                  minHeight: 36,
                  boxShadow: "none",
                  minWidth: 116,
                }}
              >
                {loading ? "Calculating..." : "Calculate"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Stack>
  );
}
